import { useState } from 'react';
import { Plus, Phone, Mail, DollarSign, Trash2 } from 'lucide-react';
import { AuditHistoryButton } from './AuditHistoryModal';
import './ClientManagement.css';

export default function ClientManagement({ clients, onAddClient, onDeleteClient }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    phone: '',
    email: '',
    hourlyRate: '',
    address: '',
  });
  const [formError, setFormError] = useState('');

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormError('');
  };

  const handleAddClient = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setFormError('Bedrijfsnaam is verplicht');
      return;
    }

    onAddClient({
      name: formData.name.trim(),
      contactPerson: formData.contactPerson.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : 0,
      address: formData.address.trim(),
    });

    setFormData({
      name: '',
      contactPerson: '',
      phone: '',
      email: '',
      hourlyRate: '',
      address: '',
    });
    setIsModalOpen(false);
  };

  return (
    <div className="client-management">
      <div className="clients-header">
        <div>
          <h2>Klanten & Bedrijven</h2>
          <p>Beheer je klantrelaties en tarieven</p>
        </div>
        <button className="add-client-btn" onClick={() => setIsModalOpen(true)}>
          <Plus size={18} />
          Nieuwe Klant
        </button>
      </div>

      <div className="clients-grid">
        {clients.length > 0 ? (
          clients.map((client) => (
            <div key={client.id} className="client-card">
              <div className="client-header">
                <h3>{client.name}</h3>
                <div className="client-header-actions">
                  <AuditHistoryButton entityType="client" entityId={client.id} label={client.name} iconOnly />
                  <button
                    className="delete-btn"
                    onClick={() => onDeleteClient(client.id)}
                    title="Klant verwijderen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="client-info">
                {client.contactPerson && (
                  <div className="info-row">
                    <span className="label">Contactpersoon</span>
                    <span className="value">{client.contactPerson}</span>
                  </div>
                )}

                <div className="info-row">
                  <Phone size={14} />
                  <a href={`tel:${client.phone}`}>{client.phone}</a>
                </div>

                <div className="info-row">
                  <Mail size={14} />
                  <a href={`mailto:${client.email}`}>{client.email}</a>
                </div>

                {client.hourlyRate > 0 && (
                  <div className="info-row">
                    <DollarSign size={14} />
                    <span>€{client.hourlyRate.toFixed(2)}/uur</span>
                  </div>
                )}

                {client.address && (
                  <div className="info-row full">
                    <span className="label">Adres</span>
                    <span className="value address">{client.address}</span>
                  </div>
                )}
              </div>

              <div className="client-stats">
                <div className="stat">
                  <span className="stat-value">—</span>
                  <span className="stat-label">Actieve Medewerkers</span>
                </div>
                <div className="stat">
                  <span className="stat-value">—</span>
                  <span className="stat-label">Totaal Uren</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-clients">
            <p>Nog geen klanten toegevoegd. Klik op "Nieuwe Klant" om te beginnen.</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Nieuwe Klant</h3>
              <button
                className="modal-close"
                onClick={() => setIsModalOpen(false)}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAddClient} className="client-form">
              <div className="form-group">
                <label htmlFor="name">Bedrijfsnaam</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  placeholder="bijv. Keppel Verolme"
                  value={formData.name}
                  onChange={handleFormChange}
                  autoFocus
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactPerson">Contactpersoon</label>
                  <input
                    id="contactPerson"
                    type="text"
                    name="contactPerson"
                    placeholder="bijv. Jan de Vries"
                    value={formData.contactPerson}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Telefoon</label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    placeholder="+31 10 1234567"
                    value={formData.phone}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">E-mailadres</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="contact@bedrijf.nl"
                    value={formData.email}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="hourlyRate">Uurtarief (€)</label>
                  <input
                    id="hourlyRate"
                    type="number"
                    name="hourlyRate"
                    placeholder="25.50"
                    step="0.01"
                    min="0"
                    value={formData.hourlyRate}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className="form-group full">
                <label htmlFor="address">Adres</label>
                <input
                  id="address"
                  type="text"
                  name="address"
                  placeholder="Straat, Plaats"
                  value={formData.address}
                  onChange={handleFormChange}
                />
              </div>

              {formError && <div className="form-error">{formError}</div>}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setIsModalOpen(false)}
                >
                  Annuleren
                </button>
                <button type="submit" className="btn-submit">
                  <Plus size={16} />
                  Nieuwe Klant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
